import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { downloadVideo, setVideoCacheEntry } from '../helpers';

export const PLAYBACK_RATES = [1, 1.5, 2];

const DEFAULT_PLAYBACK_RATE = 1;

const INITIAL_LOAD_STATE = {
  blobUrl: null,
  isBuffering: true,
  progress: 0,
  isReady: false,
  hasError: false,
};

const loadReducer = (state, action) => {
  switch (action.type) {
    case 'RESET':
      return {
        blobUrl: null,
        isBuffering: false,
        progress: 0,
        isReady: false,
        hasError: false,
      };
    case 'CACHE_HIT':
      return {
        blobUrl: action.blobUrl,
        isBuffering: false,
        progress: 1,
        isReady: false,
        hasError: false,
      };
    case 'DOWNLOAD_START':
      return {
        blobUrl: null,
        isBuffering: true,
        progress: 0,
        isReady: false,
        hasError: false,
      };
    case 'DOWNLOAD_PROGRESS':
      return { ...state, progress: action.progress };
    case 'DOWNLOAD_COMPLETE':
      return {
        blobUrl: action.blobUrl,
        isBuffering: false,
        progress: 1,
        isReady: false,
        hasError: false,
      };
    case 'DOWNLOAD_ERROR':
      return { ...state, isBuffering: false, hasError: true };
    case 'READY':
      return { ...state, isReady: true };
    default:
      return state;
  }
};

const useVideoPlayer = (url, nextUrl) => {
  const videoRef = useRef(null);
  const cacheRef = useRef(new Map());
  const controllersRef = useRef(new Set());
  const activeUrlRef = useRef(null);
  const nextUrlRef = useRef(null);
  const playbackRateRef = useRef(DEFAULT_PLAYBACK_RATE);
  const [loadState, dispatch] = useReducer(loadReducer, INITIAL_LOAD_STATE);
  const [playbackRate, setPlaybackRate] = useState(DEFAULT_PLAYBACK_RATE);
  const { blobUrl, isBuffering, progress, isReady, hasError } = loadState;

  activeUrlRef.current = url;
  nextUrlRef.current = nextUrl;

  // Prefetch the next chunk — called only after the current chunk is ready, so downloads
  // run sequentially and don't compete for bandwidth with the chunk the reviewer is watching.
  const prefetchNext = useCallback(() => {
    const cache = cacheRef.current;
    const controllers = controllersRef.current;
    const target = nextUrlRef.current;
    const currentUrl = activeUrlRef.current;

    if (!target || target === currentUrl || cache.has(target)) return;

    const ctrl = new AbortController();

    controllers.add(ctrl);
    downloadVideo(target, { signal: ctrl.signal })
      .then(b => {
        controllers.delete(ctrl);
        if (ctrl.signal.aborted) {
          URL.revokeObjectURL(b);

          return;
        }
        setVideoCacheEntry({
          cache,
          url: target,
          blobUrl: b,
          protectedUrl: activeUrlRef.current,
        });
      })
      .catch(() => controllers.delete(ctrl));
  }, []);

  useEffect(() => {
    const cache = cacheRef.current;
    const controllers = controllersRef.current;

    if (!url) {
      dispatch({ type: 'RESET' });

      return;
    }

    if (cache.has(url)) {
      dispatch({ type: 'CACHE_HIT', blobUrl: cache.get(url) });
      prefetchNext();

      return;
    }

    const mainCtrl = new AbortController();

    controllers.add(mainCtrl);
    dispatch({ type: 'DOWNLOAD_START' });

    downloadVideo(url, {
      onProgress: p => dispatch({ type: 'DOWNLOAD_PROGRESS', progress: p }),
      signal: mainCtrl.signal,
    })
      .then(b => {
        controllers.delete(mainCtrl);
        if (mainCtrl.signal.aborted) {
          URL.revokeObjectURL(b);

          return;
        }
        setVideoCacheEntry({
          cache,
          url,
          blobUrl: b,
          protectedUrl: activeUrlRef.current,
        });
        dispatch({ type: 'DOWNLOAD_COMPLETE', blobUrl: b });
        prefetchNext();
      })
      .catch(err => {
        controllers.delete(mainCtrl);
        if (err.name === 'AbortError') return;
        dispatch({ type: 'DOWNLOAD_ERROR' });
      });

    return () => {
      mainCtrl.abort();
    };
  }, [url, prefetchNext]);

  useEffect(() => {
    const cache = cacheRef.current;
    const controllers = controllersRef.current;

    return () => {
      controllers.forEach(c => c.abort());
      controllers.clear();
      cache.forEach(u => URL.revokeObjectURL(u));
      cache.clear();
    };
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;

    if (!video) return;
    video.playbackRate = playbackRateRef.current;
    // WebM files from MediaRecorder ship without a duration header; seek to end to force
    // calculation. Runs against the in-memory blob so it resolves instantly.
    if (Number.isFinite(video.duration)) {
      dispatch({ type: 'READY' });

      return;
    }

    const fallbackTimeout = setTimeout(() => dispatch({ type: 'READY' }), 3000);

    video.addEventListener(
      'seeked',
      () => {
        clearTimeout(fallbackTimeout);
        video.currentTime = 0;
        dispatch({ type: 'READY' });
      },
      { once: true },
    );
    video.currentTime = 1e101;
  }, []);

  const changePlaybackRate = useCallback(rate => {
    playbackRateRef.current = rate;
    setPlaybackRate(rate);
    if (videoRef.current) videoRef.current.playbackRate = rate;
  }, []);

  const showLoading = isBuffering || !isReady;
  const src = blobUrl || undefined;
  const controls = !showLoading;

  const videoProps = useMemo(
    () => ({ ref: videoRef, src, controls, onLoadedMetadata: handleLoadedMetadata }),
    [src, controls, handleLoadedMetadata],
  );

  return {
    videoProps,
    isBuffering,
    progress,
    showLoading,
    hasError,
    playbackRate,
    setPlaybackRate: changePlaybackRate,
  };
};

export default useVideoPlayer;
